import { Link } from 'react-router-dom'
import { PlayLogo } from '@/components/brand/PlayLogo'
import { DiscordSupportLink } from '@/components/DiscordSupportLink'
import { isViewer } from '@/components/auth/isViewer'
import { useLeaveGuard, type BeforeLeave } from '@/components/siteLeave'
import { useAuth } from '@/state/AuthContext'
import './site-footer.css'

/** The pages the footer leads to: the company, the docs and the policies. Upcoming events (/explore) is parked. */
const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
  { to: '/insights', label: 'News' },
  { to: '/docs/mcp', label: 'CLI, MCP & skills' },
  { to: '/docs/chatgpt', label: 'ChatGPT guide' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
] as const

/** The one footer, the same on every page that has one: the site, the docs, the app, sign-in and the event workspace
 *  (design/DESIGN.md, One shell). The compact mark goes home as the header's logo does (a host's events, everyone
 *  else's home page), then the brand line, Discord support, the site's links and the copyright. `beforeLeave` is
 *  asked before the mark or a link is followed in this tab, as with SiteHeader's; Discord leaves the app and passes
 *  through. It has no sign-in link: the header has Log in. */
export function SiteFooter({ beforeLeave }: { beforeLeave?: BeforeLeave }) {
  const follow = useLeaveGuard(beforeLeave)
  const { isAuthenticated, authReady, user, organization } = useAuth()
  const host = isAuthenticated && authReady && !isViewer(organization, user)
  const home = host ? { to: '/app', label: 'StreamBridge events' } : { to: '/', label: 'StreamBridge home' }
  return (
    <footer className="play-site-footer">
      <Link className="play-site-footer__home" to={home.to} onClick={(event) => follow(event, home.to)} aria-label={home.label}><PlayLogo markOnly /></Link>
      <p>Sweat. Stream. Host. Watch.</p>
      <div>
        <DiscordSupportLink />
        <nav aria-label="Footer">
          <ul className="play-site-footer__links">
            {FOOTER_LINKS.map((link) => (
              <li key={link.to}><Link to={link.to} onClick={(event) => follow(event, link.to)}>{link.label}</Link></li>
            ))}
          </ul>
        </nav>
      </div>
      <small>© {new Date().getFullYear()} StreamBridge</small>
    </footer>
  )
}
